<template>
  <section class="contact-editor">
    <header>
      <div>
        <h4>Company & contact</h4>
        <p>Account details and the person to contact.</p>
      </div>
      <b-button v-if="!editing" variant="outline-secondary" @click="begin">Edit details</b-button>
    </header>
    <div v-if="!editing" class="contact-summary">
      <section aria-label="Company details">
        <h5>Company details</h5>
        <dl class="contact-facts">
          <div v-for="field in fields.filter((item) => ['name', 'clientNumber', 'shortName', 'contractParty'].includes(item.key))" :key="field.key">
            <dt>{{ field.label }}</dt><dd>{{ company[field.key] || '—' }}</dd>
          </div>
          <div><dt>Assigned salesman</dt><dd>{{ salesmanName }}</dd></div>
        </dl>
      </section>
      <section aria-label="Contact person">
        <h5>Contact person</h5>
        <dl class="contact-facts">
          <div><dt>Name</dt><dd>{{ [company.contactName, company.contactLastName].filter(Boolean).join(' ') || '—' }}</dd></div>
          <div><dt>Email address</dt><dd>{{ company.email || '—' }}</dd></div>
          <div><dt>Phone number</dt><dd>{{ company.phoneNumber || '—' }}</dd></div>
        </dl>
      </section>
    </div>
    <form v-else @submit.prevent="save">
      <fieldset :disabled="saving">
        <legend class="visually-hidden">Company and contact details</legend>
        <div class="contact-grid">
          <div v-for="field in fields" :key="field.key">
            <label :for="`${company.id}-${field.key}`">{{ field.label }}</label><input
              :id="`${company.id}-${field.key}`"
              v-model="draft[field.key]"
              :type="field.type || 'text'"
              :required="field.key === 'email'"
              class="form-control"
            />
          </div>
          <div>
            <label :for="`${company.id}-salesman`">Assigned salesman</label><select :id="`${company.id}-salesman`" v-model="salesmanId" class="form-control">
              <option value="" :disabled="!!company.salesmanId">Not assigned</option>
              <option
                v-if="company.salesmanId && !staffs.some((staff) => staff.id === company.salesmanId)"
                :value="company.salesmanId"
              >
                {{ salesmanName }}
              </option>
              <option v-for="staff in staffs" :key="staff.id || staff.email" :value="staff.id">
                {{ staff.firstName ? `${staff.firstName} ${staff.lastName || ''}` : staff.email }}
              </option>
            </select>
          </div>
        </div>
      </fieldset>
      <p v-if="error" class="text-danger mt-3" role="alert">{{ error }}</p>
      <div class="contact-actions">
        <span>Changes apply only to these details.</span><b-button class="btn-grey" :disabled="saving" @click="cancel">Cancel</b-button><b-button type="submit" class="btn-green" :disabled="saving || !changed">
          {{
            saving ? 'Saving…' : 'Save details'
          }}
        </b-button>
      </div>
    </form>
    <p v-if="saved && !editing" role="status" class="text-success mt-3 mb-0">Company details saved.</p>
  </section>
</template>

<script setup lang="ts">
import type { CustomerContactEditorProps, CustomerInlineField } from '@/types/Models/Company'
import { BButton } from 'bootstrap-vue-next'
import { computed, ref } from 'vue'
import { useCustomersStore } from '@/store/customers'
import { usePackingSlipStore } from '@/store/packingSlip'
import CompanyProxy from '@/proxies/CompanyProxy'

const props = defineProps<CustomerContactEditorProps>()
const customers = useCustomersStore()
const packingSlip = usePackingSlipStore()
const fields: CustomerInlineField[] = [
  { key: 'name', label: 'Company' },
  { key: 'clientNumber', label: 'Client №' },
  { key: 'shortName', label: 'Short name' },
  { key: 'contractParty', label: 'Contract party' },
  { key: 'contactName', label: 'First name' },
  { key: 'contactLastName', label: 'Last name' },
  { key: 'email', label: 'Email address', type: 'email' },
  { key: 'phoneNumber', label: 'Phone number', type: 'tel' },
]
const editing = ref(false)
const saving = ref(false)
const saved = ref(false)
const error = ref('')
const draft = ref<Record<string, string>>({})
const initial = ref<Record<string, string>>({})
const salesmanId = ref('')
const staffs = computed(() => packingSlip.staffs || [])
const salesmanName = computed(() => {
  const salesman = props.company.salesman
  return salesman
    ? [salesman.first_name, salesman.last_name].filter(Boolean).join(' ') || salesman.email || 'Not assigned'
    : 'Not assigned'
})
const changed = computed(
  () =>
    fields.some((field) => draft.value[field.key] !== initial.value[field.key]) ||
    salesmanId.value !== (props.company.salesmanId || ''),
)
function begin() {
  initial.value = Object.fromEntries(fields.map((field) => [field.key, props.company[field.key] || '']))
  draft.value = { ...initial.value }
  salesmanId.value = props.company.salesmanId || ''
  editing.value = true
  saved.value = false
  error.value = ''
  if (!staffs.value.length) packingSlip.fetchStaffs()
}
function cancel() {
  editing.value = false
  error.value = ''
}
async function save() {
  if (saving.value || !changed.value) return
  saving.value = true
  error.value = ''
  const data: Record<string, string> = Object.fromEntries(
    fields
      .filter((field) => draft.value[field.key] !== initial.value[field.key])
      .map((field) => [field.key, draft.value[field.key]]),
  )
  if (salesmanId.value !== (props.company.salesmanId || '')) data.assignedSalesman = salesmanId.value
  try {
    await new CompanyProxy().update(props.company.id, data)
    const customer = customers.listCustomers?.find((item) => item.id === props.company.id)
    if (customer) {
      const { assignedSalesman, ...details } = data
      Object.assign(customer, details)
      if ('shortName' in details) customer.shortName = details.shortName.toUpperCase()
      if (assignedSalesman) {
        const staff = staffs.value.find((item) => item.id === assignedSalesman)
        customer.salesmanId = assignedSalesman
        if (staff) customer.salesman = { first_name: staff.firstName, last_name: staff.lastName, email: staff.email }
      }
    }
    editing.value = false
    saved.value = true
  } catch {
    error.value = 'Could not save these details. Check the values and try again. Your changes are still here.'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.contact-editor header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 16px;
  margin-bottom: 18px;
}
h4 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 5px;
}
header p {
  margin: 0;
  font-size: 13px;
  color: #59635e;
}
.contact-summary { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.contact-summary section + section { border-left: 1px solid #e1e6e3; padding-left: 24px; }
.contact-summary h5 { font-size: 14px; font-weight: 600; margin-bottom: 16px; }
.contact-facts { display: grid; gap: 14px; margin: 0; }
.contact-facts > div { display: grid; grid-template-columns: 135px minmax(0, 1fr); gap: 12px; }
@media (max-width: 700px) {
  .contact-summary { grid-template-columns: 1fr; }
  .contact-summary section + section { border-left: 0; border-top: 1px solid #e1e6e3; padding: 20px 0 0; }
  .contact-facts > div { grid-template-columns: 1fr; gap: 4px; }
}
.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 24px;
  margin: 0;
}
dt,
label {
  font-size: 12px;
  font-weight: 400;
  color: #59635e;
  margin: 0 0 5px;
}
dd {
  margin: 0;
  overflow-wrap: anywhere;
}
input,
select {
  min-width: 0;
  font-size: 14px;
}
fieldset {
  min-width: 0;
}
.contact-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #e1e6e3;
  padding-top: 16px;
  margin-top: 20px;
}
.contact-actions span {
  margin-right: auto;
  color: #59635e;
  font-size: 12px;
}
@media (max-width: 1000px) {
  .contact-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 600px) {
  .contact-grid {
    grid-template-columns: 1fr;
  }
  .contact-actions {
    flex-wrap: wrap;
  }
  .contact-actions span {
    flex-basis: 100%;
  }
}
</style>
